import React, { useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Toggle,
  Dropdown,
  IDropdownOption,
  SearchBox,
  Checkbox,
} from '@fluentui/react';
import { selectTemplate, updateEmailTemplate } from '../../Redux/EmailTemplates/actions';
import { selectEmailTemplatesState } from './emailTemplatesState';
import { EmailTemplateUpdate } from '../../models/EmailTemplate';
import { EmailTemplate } from '../../models/EmailTemplate';
import { useGatedColumns } from '../../Components/PBT/Gated/useGatedColumns';
import { styles } from './EmailTemplateAdmin.styled';

// Maps DetailsList column keys to their capability under page.emailTemplates/table.emailTemplates.
const EMAIL_COLUMN_CAPABILITY: Record<string, string> = {
  templateCode: 'col.email.code',
  templateName: 'col.email.name',
  recipientType: 'col.email.recipient',
  triggerEvent: 'col.email.trigger',
  suppressionRule: 'col.email.suppression',
  hasAttachments: 'col.email.attachments',
  isActive: 'col.email.active',
  lastModifiedDate: 'col.email.lastModified',
};

const recipientOptions: IDropdownOption[] = [
  { key: 'All', text: 'All' },
  { key: 'Employee', text: 'Employee' },
  { key: 'Manager', text: 'Manager' },
  { key: 'HRC', text: 'HRC' },
];

type SortField = 'templateName' | 'lastModifiedDate';

function getBadgeClass(recipientType: string): string {
  switch (recipientType) {
    case 'Manager':
      return styles.badgeManager;
    case 'Employee':
      return styles.badgeEmployee;
    case 'HRC':
      return styles.badgeHRC;
    default:
      return styles.badgeManager;
  }
}

export const EmailTemplateList: React.FC = () => {
  const dispatch = useDispatch();
  const templates = useSelector((state: { dynamic?: Record<string, unknown> }) => selectEmailTemplatesState(state).templates);

  const [recipientFilter, setRecipientFilter] = useState<string>('All');
  const [searchText, setSearchText] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortField, setSortField] = useState<SortField>('templateName');
  const [sortDescending, setSortDescending] = useState(false);

  const filteredTemplates = useMemo(() => {
    let filtered = [...templates];

    if (recipientFilter !== 'All') {
      filtered = filtered.filter((t) => t.recipientType === recipientFilter);
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.templateCode.toLowerCase().includes(lower) ||
          t.templateName.toLowerCase().includes(lower) ||
          t.triggerEvent.toLowerCase().includes(lower)
      );
    }

    if (!showInactive) {
      filtered = filtered.filter((t) => t.isActive);
    }

    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'templateName') {
        cmp = a.templateName.localeCompare(b.templateName);
      } else if (sortField === 'lastModifiedDate') {
        cmp = new Date(a.lastModifiedDate).getTime() - new Date(b.lastModifiedDate).getTime();
      }
      return sortDescending ? -cmp : cmp;
    });

    return filtered;
  }, [templates, recipientFilter, searchText, showInactive, sortField, sortDescending]);

  const handleColumnClick = useCallback(
    (_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
      const field = column.data as SortField | undefined;
      if (field) {
        if (sortField === field) {
          setSortDescending(!sortDescending);
        } else {
          setSortField(field);
          setSortDescending(false);
        }
      }
    },
    [sortField, sortDescending]
  );

  const handleRowClick = useCallback(
    (item?: EmailTemplate) => {
      if (item) {
        dispatch(selectTemplate({ ...item }));
      }
    },
    [dispatch]
  );

  const handleToggleActive = useCallback(
    (item: EmailTemplate, checked: boolean) => {
      const update: EmailTemplateUpdate = {
        emailSubject: item.emailSubject,
        emailBody: item.emailBody,
        ccRecipients: item.ccRecipients,
        bccRecipients: item.bccRecipients,
        isActive: checked,
      };
      dispatch(updateEmailTemplate(item.templateCode, update));
    },
    [dispatch]
  );

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: 'templateCode',
        name: 'Template Code',
        fieldName: 'templateCode',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: EmailTemplate) => (
          <span
            style={{ fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#0078d4', cursor: 'pointer' }}
            onClick={() => handleRowClick(item)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(item); }}
          >
            {item.templateCode}
          </span>
        ),
      },
      {
        key: 'templateName',
        name: 'Template Name',
        fieldName: 'templateName',
        minWidth: 180,
        maxWidth: 260,
        isResizable: true,
        isSorted: sortField === 'templateName',
        isSortedDescending: sortField === 'templateName' && sortDescending,
        onColumnClick: handleColumnClick,
        data: 'templateName' as SortField,
        onRender: (item: EmailTemplate) => (
          <span
            style={{ color: '#0078d4', cursor: 'pointer' }}
            onClick={() => handleRowClick(item)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(item); }}
          >
            {item.templateName}
          </span>
        ),
      },
      {
        key: 'recipientType',
        name: 'Recipient',
        fieldName: 'recipientType',
        minWidth: 90,
        maxWidth: 110,
        isResizable: true,
        onRender: (item: EmailTemplate) => (
          <span className={getBadgeClass(item.recipientType)} role="status" aria-label={`Recipient: ${item.recipientType}`}>
            {item.recipientType}
          </span>
        ),
      },
      {
        key: 'triggerEvent',
        name: 'Trigger Event',
        fieldName: 'triggerEvent',
        minWidth: 160,
        maxWidth: 220,
        isResizable: true,
        onRender: (item: EmailTemplate) => (
          <span style={{ fontFamily: 'Consolas, monospace', fontSize: '12px' }}>
            {item.triggerEvent}
          </span>
        ),
      },
      {
        key: 'suppressionRule',
        name: 'Suppression',
        fieldName: 'suppressionRule',
        minWidth: 100,
        maxWidth: 160,
        isResizable: true,
      },
      {
        key: 'hasAttachments',
        name: 'Attachments',
        fieldName: 'hasAttachments',
        minWidth: 80,
        maxWidth: 100,
        onRender: (item: EmailTemplate) => (
          <span>{item.hasAttachments ? 'Yes' : 'No'}</span>
        ),
      },
      {
        key: 'isActive',
        name: 'Active',
        fieldName: 'isActive',
        minWidth: 60,
        maxWidth: 80,
        onRender: (item: EmailTemplate) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={item.isActive}
              onChange={(_ev, checked) => handleToggleActive(item, !!checked)}
              ariaLabel={`Toggle active state for ${item.templateName}`}
              styles={{ root: { margin: 0 } }}
            />
          </div>
        ),
      },
      {
        key: 'lastModifiedDate',
        name: 'Last Modified',
        fieldName: 'lastModifiedDate',
        minWidth: 140,
        maxWidth: 180,
        isResizable: true,
        isSorted: sortField === 'lastModifiedDate',
        isSortedDescending: sortField === 'lastModifiedDate' && sortDescending,
        onColumnClick: handleColumnClick,
        data: 'lastModifiedDate' as SortField,
        onRender: (item: EmailTemplate) => {
          const date = new Date(item.lastModifiedDate);
          return (
            <span>
              {date.toLocaleDateString()} by {item.lastModifiedBy}
            </span>
          );
        },
      },
    ],
    [sortField, sortDescending, handleColumnClick, handleToggleActive]
  );

  // Feature-tree overlay: drop hidden columns, mask read-only/sensitive ones.
  const gatedColumns = useGatedColumns<EmailTemplate>(
    'page.emailTemplates/table.emailTemplates',
    EMAIL_COLUMN_CAPABILITY,
    columns,
  );

  return (
    <div className={styles.listContainer}>
      <div className={styles.filterBar}>
        <span className={styles.filterLabel}>Recipient Type:</span>
        <Dropdown
          selectedKey={recipientFilter}
          options={recipientOptions}
          onChange={(_ev, option) => setRecipientFilter(option?.key as string || 'All')}
          styles={{ root: { width: 150 } }}
          ariaLabel="Filter by recipient type"
        />
        <SearchBox
          placeholder="Search templates..."
          value={searchText}
          onChange={(_ev, newValue) => setSearchText(newValue || '')}
          className={styles.searchBox}
          ariaLabel="Search email templates"
        />
        <Checkbox
          label="Show inactive"
          checked={showInactive}
          onChange={(_ev, checked) => setShowInactive(!!checked)}
          styles={{ root: { marginLeft: 'auto' } }}
        />
      </div>

      {filteredTemplates.length === 0 ? (
        <div className={styles.emptyState}>
          <span>No email templates found matching your criteria.</span>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <DetailsList
            items={filteredTemplates}
            columns={gatedColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            onActiveItemChanged={undefined}
            ariaLabel="Email templates list"
            styles={{
              root: { overflow: 'visible' },
            }}
          />
        </div>
      )}
    </div>
  );
};
